import { Controller } from "@hotwired/stimulus";
import { loadStripe } from "@stripe/stripe-js";
import Rails from "@rails/ujs";

// Replaces Plaid Link with Stripe Financial Connections for bank account setup.
// Uses collectBankAccountForSetup() which opens FC modal via SetupIntent.
export default class extends Controller {
  static targets = ["token", "errors"];

  static values = {
    publishableKey: String,
    setupIntentUrl: String,
  };

  async connect() {
    this.stripe = await loadStripe(this.publishableKeyValue);
    this.connected = false;
    this.loading = false;
  }

  // Public action — opens Financial Connections modal
  async open(event) {
    event.preventDefault();
    if (this.loading) return;
    this.loading = true;
    this.clearErrors();

    try {
      // Fetch a SetupIntent client_secret for us_bank_account from the server
      const response = await fetch(this.setupIntentUrlValue, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": Rails.csrfToken(),
        },
      });

      if (!response.ok) {
        const data = await response.json();
        this.showError(data.error || "Failed to initialize bank setup");
        return;
      }

      const { client_secret } = await response.json();

      // Open Stripe Financial Connections modal
      const result = await this.stripe.collectBankAccountForSetup({
        clientSecret: client_secret,
        params: {
          payment_method_type: "us_bank_account",
          payment_method_data: {
            billing_details: { name: this.getAccountHolderName() },
          },
        },
      });

      if (result.error) {
        this.showError(result.error.message);
        return;
      }

      const setupIntent = result.setupIntent;

      if (setupIntent.status === "requires_payment_method") {
        // User closed the FC modal without selecting a bank
        return;
      }

      // Confirm ACH mandate for instant-verification bank accounts
      if (setupIntent.status === "requires_confirmation") {
        const confirmResult =
          await this.stripe.confirmUsBankAccountSetup(client_secret);
        if (confirmResult.error) {
          this.showError(confirmResult.error.message);
          return;
        }
      }

      this.tokenTarget.value = setupIntent.id;
      this.connected = true;

      // Update UI to show success
      document
        .querySelector(".payment_methods__nav-tabs")
        ?.classList?.add("d-none");
      this.showElement(".payment_methods__add_bank-success");
      this.hideElement(".payment_methods__add_bank-new");
      this.hideElement(".payment_methods__add_bank-fail");
      this.hideElement(".payment_methods__add_bank-instructions");
    } catch (error) {
      this.showError("Bank connection failed. Please try again.");
    } finally {
      this.loading = false;
    }
  }

  // Public method for form controller submission validation
  validate() {
    if (!this.connected || !this.tokenTarget.value) {
      this.showError("Please connect your bank account before submitting.");
      return false;
    }
    return true;
  }

  // Private helpers
  getAccountHolderName() {
    // Try to find the user's name from the form (sign-up fields or signed-in display)
    const firstNameInput = document.querySelector('[name*="[first_name]"]');
    const lastNameInput = document.querySelector('[name*="[last_name]"]');
    if (firstNameInput && lastNameInput) {
      const name = `${firstNameInput.value} ${lastNameInput.value}`.trim();
      if (name) return name;
    }
    return "Account Holder";
  }

  showError(message) {
    if (this.hasErrorsTarget) {
      this.errorsTarget.textContent = message;
      this.errorsTarget.hidden = false;
      this.errorsTarget.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    this.showElement(".payment_methods__add_bank-fail");
  }

  clearErrors() {
    if (this.hasErrorsTarget) {
      this.errorsTarget.textContent = "";
      this.errorsTarget.hidden = true;
    }
    this.hideElement(".payment_methods__add_bank-fail");
  }

  hideElement(selector) {
    this.element.querySelector(selector)?.classList.add("d-none");
  }

  showElement(selector) {
    this.element.querySelector(selector)?.classList.remove("d-none");
  }
}
